
import { Order } from '../types';
import { getGlobalCache, getStoreCache, setGlobalCache, setStoreCache } from './supabaseCache';
import { normalizeAddress, geocodeAddress as cachedGeocodeAddress } from './maps/geocoding';
import { getDistanceMatrix } from './maps/distance';
import { getStreetViewMetadata } from './maps/streetView';
import { DistanceService } from './parserService';
import { getHereGeocodingViaEdge, getHereMapsDistance } from './hereMaps';

/**
 * v35.1: SaaS Intelligence Engine (Dual-Layer Expansion)
 * 1. Global (matrix_cache): Onde fica o endereço (compartilhado entre todas as lojas).
 * 2. Local (route_intelligence): Distância/Duração em relação ao HUB da loja.
 * v2025.02.08: Added HERE Maps support as primary provider with Google fallback
 */
export const enrichOrderWithLocation = async (
    order: Order,
    config: { 
        lat: number, 
        lng: number, 
        googleMapsApiKey?: string, 
        hereMapsApiKey?: string,
        storeId: string, 
        storeCity?: string 
    }
): Promise<Order> => {
    if (order.type === 'TAKEOUT') return order;

    // Chave única para o endereço (NORMALIZADA com mapas/geocoding)
    const normalizedKey = normalizeAddress(order.address);
    const addressKey = btoa(unescape(encodeURIComponent(normalizedKey))).substring(0, 100);

    // Refs
    const globalKey = `geo_${addressKey}`;
    const storeCacheKey = addressKey;

    try {
        // 1. TENTA RECUPERAR AMBOS OS CACHES
        const [gSnap, sSnap] = await Promise.all([
            getGlobalCache<any>(globalKey),
            getStoreCache<any>(config.storeId, storeCacheKey, 'route_intelligence')
        ]);

        let lat = order.lat, lng = order.lng, panoId = order.streetViewPanoId;
        let distance = order.distance || 0;
        let duration = order.estimatedDuration || 0;

        // Se existe no Cache Global, recuperamos as coordenadas e foto
        if (gSnap) {
            const g = gSnap;
            lat = g.lat; lng = g.lng; panoId = g.panoId;
        }

        // Se existe no Cache da Loja, validamos se o HUB não mudou
        if (sSnap) {
            const s = sSnap;
            const hubMoved = Math.abs(s.originLat - config.lat) > 0.0005 || Math.abs(s.originLng - config.lng) > 0.0005;

            if (!hubMoved) {
                distance = s.roadDistanceKm;
                duration = s.baseDurationMins;
            }
        }

        // 2. SE FALTA INFORMAÇÃO CRÍTICA, CHAMA O TURBO INTELLIGENCE (HERE FIRST)
        if (!lat || !lng || distance === 0) {
            if (config.googleMapsApiKey || config.hereMapsApiKey) {
                const result = await fetchTurboIntelligence(
                    order.address, 
                    config.lat, 
                    config.lng, 
                    config.googleMapsApiKey,
                    config.hereMapsApiKey
                );
                if (result) {
                    lat = result.lat; lng = result.lng; panoId = result.panoId;
                    distance = result.distance; duration = result.duration;

                    // Salva Global (DNA do Endereço)
                    await setGlobalCache(globalKey, 'geocode', {
                        lat, lng, panoId, placeId: result.placeId,
                        neighborhood: result.neighborhood, city: result.city,
                        updatedAt: Date.now(),
                        normalizedKey,
                        address: order.address
                    });

                    // Salva Local (DNA da Viagem)
                    await setStoreCache(config.storeId, storeCacheKey, 'route_intelligence', {
                        roadDistanceKm: result.distance,
                        baseDurationMins: result.duration,
                        originLat: config.lat,
                        originLng: config.lng,
                        lastUpdated: Date.now()
                    });
                }
            }
        }

        return {
            ...order, lat, lng,
            distance: distance || DistanceService.getDistanceSync(config.lat, config.lng, lat || 0, lng || 0),
            estimatedDuration: duration || (distance ? distance * 3 : 0),
            streetViewPanoId: panoId,
            isRoadDistance: distance > 0,
            geocodingFailed: !lat
        };

    } catch (e) { console.error("[SAL-RECON] Erro na inteligência:", e); }
    return { ...order, geocodingFailed: true };
};

/**
 * v2025.02.08: Enhanced Turbo Intelligence with HERE as primary provider
 * Falls back to Google Maps when HERE fails or is not configured
 */
const fetchTurboIntelligence = async (
    address: string,
    hLat: number,
    hLng: number,
    googleApiKey?: string,
    hereApiKey?: string
) => {
    // A. Geocoding - Try HERE first
    let geo: { lat: number; lng: number } | null = null;
    if (hereApiKey) {
        const hereGeo = await getHereGeocodingViaEdge(address, hereApiKey);
        if (hereGeo) {
            geo = { lat: hereGeo.lat, lng: hereGeo.lng };
        }
    }

    // Fallback to Google Geocoding
    if (!geo && googleApiKey) {
        geo = await cachedGeocodeAddress(address, hLat, hLng, googleApiKey);
    }

    if (!geo) return null;
    const { lat, lng } = geo;

    // B. Distance Matrix - Try HERE first
    let roadDistance = 0, duration = 0;
    if (hereApiKey) {
        const hereDist = await getHereMapsDistance(hLat, hLng, lat, lng, hereApiKey);
        if (hereDist && hereDist.distanceKm > 0) {
            roadDistance = hereDist.distanceKm;
            duration = hereDist.durationMins;
        }
    }

    // Fallback to Google Distance Matrix
    if (roadDistance === 0 && googleApiKey) {
        const googleDist = await getDistanceMatrix(hLat, hLng, lat, lng, googleApiKey);
        if (googleDist) {
            roadDistance = googleDist.distanceKm;
            duration = googleDist.minutes;
        }
    }

    // C. Street View (Google Only - HERE doesn't provide Street View)
    let panoId: string | undefined;
    if (googleApiKey) {
        panoId = await getStreetViewMetadata(lat, lng, googleApiKey);
    }

    // Fallback Mock for missing parts if Cache Hit prevented full Google Response
    return {
        lat, lng, panoId, distance: roadDistance, duration,
        placeId: `chk_${lat}_${lng}`, // Pseudo-ID
        neighborhood: 'Detectado',
        city: 'Detectado'
    };
};

export const geocodeAddress = async (
    address: string, 
    hubLat: number, 
    hubLng: number, 
    googleApiKey?: string,
    hereApiKey?: string
) => {
    // v4.0: Centralized Geocoding Router with HERE Primary + Google Fallback
    const result = await cachedGeocodeAddress(address, hubLat, hubLng, googleApiKey, hereApiKey);
    if (result) return result;
    return null;
};

export const getStreetViewImageUrl = (lat: number, lng: number, apiKey: string, heading: number = 0, panoId?: string) => {
    if (!apiKey) return '';
    if (panoId) return `https://maps.googleapis.com/maps/api/streetview?size=600x400&pano=${panoId}&heading=${heading}&pitch=0&key=${apiKey}`;
    return `https://maps.googleapis.com/maps/api/streetview?size=600x400&location=${lat},${lng}&heading=${heading}&pitch=0&key=${apiKey}`;
};
